import type { QueryFunction } from '@tanstack/react-query';
import type { ApiRequestObject } from '../../lib/flotiq-client/types/api-request';
import type {
  ContentObject,
  ContentTypeDefinition,
} from '../../lib/flotiq-client/types/api-response';
import type { ListingPagination } from './hook-builders/listing-hook-builder';
import type { HookOptions } from './hook-builders/types';

import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getContentObjects, getContentType } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';

import useSelectedSpace from '../useSelectedSpace';
import useToken from '../useToken';

const DEFAULT_PARAMS = {
  defaultParams: { page: 1, limit: 10 },
  defaultOptions: {},
};

export type HookRelationData =
  | Record<
      string,
      {
        data: ContentObject[];
        titleField: string;
        contentType: ContentTypeDefinition;
      }
    >
  | Record<string, string[]>;

interface HookRelationObjectResult {
  data: HookRelationData | null;
  pagination: ListingPagination;
  isLoading: boolean;
  isFetching: boolean;
  errors: any;
  reload: () => void;
}

type HookRelationArrayResult = [
  data: HookRelationData | null,
  pagination: ListingPagination,
  isLoading: boolean,
  isFetching: boolean,
  errors: any,
  reload: () => void,
];

type HookRelationResult = HookRelationObjectResult & HookRelationArrayResult;

export const useRelations = (
  relationsRawData: { dataUrl: string }[] | null,
  defaultParams: ApiRequestObject = DEFAULT_PARAMS.defaultParams,
  defaultOptions: HookOptions = DEFAULT_PARAMS.defaultOptions,
) => {
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const {
    checkStatus = true,
    cancelUnfinishedRequests = true,
    pause = false,
    staleTime = 0,
    gcTime = 5 * 60 * 1000, //5 minutes
  } = defaultOptions || {};

  const relationsRawDataString = useMemo(() => {
    if (!relationsRawData?.length) return '[]';
    const minIdx = (defaultParams?.page - 1) * defaultParams?.limit;
    const limitedData = relationsRawData
      .filter((relation) => !!relation.dataUrl)
      .filter((_, idx) => idx >= minIdx && idx < minIdx + defaultParams?.limit);

    return JSON.stringify(
      Array.from(
        new Set(limitedData.map((relation) => relation.dataUrl).sort()),
      ),
    );
  }, [defaultParams?.limit, defaultParams?.page, relationsRawData]);

  const relationDataUris = useMemo(
    () => JSON.parse(relationsRawDataString) as string[],
    [relationsRawDataString],
  );

  const idsDict = useMemo(
    () =>
      relationDataUris.reduce(
        (acc, element) => {
          const { contentTypeName, id } =
            element.match(/(?<contentTypeName>[^/]+)\/(?<id>[^/]+)$/)?.groups ||
            {};
          if (!acc[contentTypeName]) acc[contentTypeName] = [];
          acc[contentTypeName].push(id);
          return acc;
        },
        {} as Record<string, string[]>,
      ),
    [relationDataUris],
  );

  const getRelationsData = useCallback<
    QueryFunction<any, (string | object)[], never>
  >(
    async ({ signal }) => {
      const newDict: HookRelationData = {};
      const newErrors: unknown[] = [];

      await Promise.all(
        Object.keys(idsDict).map(async (key) => {
          try {
            const requestOptions = cancelUnfinishedRequests ? { signal } : {};

            const ctoPromise = getContentObjects(
              jwt,
              space,
              {
                contentTypeName: key,
                ids: idsDict[key],
                limit: idsDict[key].length,
              },
              requestOptions,
            );
            const ctdPromise = getContentType(
              jwt,
              space,
              { contentTypeName: key },
              requestOptions,
            );
            const [ctoResult, ctdResult] = await Promise.all([
              ctoPromise,
              ctdPromise,
            ]);

            if (checkStatus) {
              if (!ctdResult.ok) {
                throw parseResponseErrors(ctdResult.body, ctdResult.status);
              } else if (!ctoResult.ok) {
                throw parseResponseErrors(ctoResult.body, ctoResult.status);
              }
            }
            const def = ctdResult.body.metaDefinition;
            const firstTextField = def.order.find(
              (field: string) =>
                def.propertiesConfig[field].inputType === 'text',
            );
            newDict[key] = {
              data: ctoResult.body.data,
              titleField: firstTextField || '',
              contentType: ctdResult.body,
            };
          } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') return;
            newErrors.push(e);
          }
        }),
      );

      return { data: newDict, errors: newErrors.length ? newErrors : null };
    },
    [cancelUnfinishedRequests, checkStatus, idsDict, jwt, space],
  );

  const enabled = !!jwt && !pause && !!relationDataUris.length;

  const {
    isLoading: requestLoading,
    data: response,
    refetch: reload,
    isPlaceholderData,
    isFetching,
  } = useQuery({
    queryKey: [
      'content-objects',
      'relations',
      { dataUris: relationDataUris },
      space || '',
    ],
    queryFn: getRelationsData,
    enabled,
    placeholderData: enabled
      ? { data: idsDict, titleField: '', contentType: null, errors: null }
      : undefined,
    staleTime,
    gcTime,
    retry: 0,
  });

  const { data, errors } = useMemo(
    () => ({
      data: response?.data || null,
      errors: response?.errors || null,
    }),
    [response],
  );

  const isLoading = requestLoading || isPlaceholderData;
  const relationsLength = relationsRawData?.length || 0;

  const pagination = useMemo(() => {
    if (!relationsLength || errors?.length) return {};

    let maxPages = Math.floor(relationsLength / defaultParams?.limit);
    if (relationsLength % defaultParams?.limit !== 0) maxPages++;

    return {
      total_pages: maxPages,
      total_count: relationsLength,
      count: relationDataUris.length,
      current_page: defaultParams?.page,
    };
  }, [
    defaultParams?.limit,
    defaultParams?.page,
    errors?.length,
    relationDataUris.length,
    relationsLength,
  ]);

  const result = [data, pagination, isLoading, isFetching, errors, reload];
  Object.assign(result, {
    data,
    pagination,
    isLoading,
    isFetching,
    errors,
    reload,
  });
  return result as HookRelationResult;
};
